import gridMixin from '@/modules/calendar/mixins/gridMixin';
import appointmentMixin from '@/components/mixins/appointmentMixin';
import renderMixin from '@satellite/components/mixins/renderMixin';
import { AppointmentStartTimeMinutes, AppointmentType } from '@nova/core';
import { DateTime, Interval } from 'luxon';
import { calculateIntervalMidpoint } from '@satellite/plugins/util.js';
import { CalendarViewEnum } from '@/enums/index.js';
import { padStart, isEqual, get } from 'lodash';
import config from '@/config/config';
import { math } from '@nova/core';
import { DEFAULT_INTERVAL_MINUTES } from '@/modules/calendar/store';
import allLocales from '@fullcalendar/core/locales-all';

/**
 * Mixin used in calendar
 * @mixin
 * @displayName Calendar Mixin
 */

const customStartTimeIntervalBuffer = 30;

export default {
  mixins: [appointmentMixin, renderMixin, gridMixin],
  props: {
    /**
     * Calendar events to display in calendar
     */
    events: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    /**
     * Selected docks to display
     */
    docks: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    }
  },
  computed: {
    CalendarViewEnum() {
      return CalendarViewEnum;
    },
    startTimeMinutesSetting() {
      return this.$selectedWarehouse?.settings?.appointmentStartTimeMinutes ?? 30;
    },
    calendarOptions() {
      return {
        locales: allLocales,
        locale: this.$i18n.locale,
        allDaySlot: false,
        dateClick: info => this.handleCalendarClick(info),
        editable: true,
        eventAllow: (info, event) => this.handleEventAllow(info, event),
        eventClassNames: function (arg) {
          const isReserve = arg.event.extendedProps?.type === AppointmentType.Reserve;
          let classes = [];
          if (arg.event.extendedProps?.status && !isReserve) {
            classes.push(`bg-${arg.event.extendedProps.status.toLowerCase()}`);
          }
          if (isReserve) {
            classes.push('bg-reserve');
          }
          return classes;
        },
        eventClick: info => this.handleEventClick(info),
        eventConstraint: 'businessHours',
        eventDidMount: info => this.addContextListenersToEvent(info),
        eventDrop: info => this.handleEventDrop(info),
        eventResize: info => this.confirmDurationChange(info),
        events: this.localEvents,
        expandRows: true,
        footerToolbar: false,
        headerToolbar: false,
        nowIndicator: true,
        schedulerLicenseKey: config.fullcalendar_key,
        slotDuration: this.intervalMinutes,
        slotLabelContent: interval => this.formatInterval(interval),
        slotLabelInterval: this.intervalMinutes,
        slotMaxTime: this.calendarTimeBounds.end,
        slotMinTime: this.calendarTimeBounds.start,
        snapDuration: this.getSnapDuration(),
        timeZone: this.$selectedWarehouse?.timezone,
        weekends: this.$shouldShowWeekends
      };
    },
    selectedDateTime() {
      return DateTime.fromISO(this.$selectedDate).setZone(this.timezone).startOf('day').toUTC();
    },
    areCustomStartTimesEnabled() {
      return (
        this.$selectedWarehouse?.settings?.appointmentStartTimeMinutes ===
        AppointmentStartTimeMinutes.CUSTOM
      );
    },
    timezone() {
      return this.$selectedWarehouse?.timezone;
    },
    calendarApi() {
      return this.ready ? this.$refs.fullCalendar.getApi() : null;
    },
    selectedWarehouseId() {
      return this.$store.getters['Calendar/selectedWarehouseId'];
    },
    selectedDate: {
      get() {
        return this.$selectedDate;
      },
      set(date) {
        return this.$store.dispatch('Calendar/setSelectedDate', {
          value: date,
          getEvents: this.ready
        });
      }
    },
    calendarTimeBounds() {
      let startTime = null;
      let endTime = null;
      const schedule = this.novaCore.deepClone(
        this.$selectedWarehouse?.schedule ?? this.novaCore.getFullyOpenSchedule()
      );
      delete schedule.version;
      delete schedule.closedIntervals;
      for (const day in schedule) {
        schedule[day].map(interval => {
          const intervalStart = DateTime.fromFormat(interval.start, 'H:mm');
          const intervalEnd = DateTime.fromFormat(interval.end, 'H:mm');

          if (!startTime) {
            startTime = intervalStart;
          }
          if (!endTime) {
            endTime = intervalEnd;
          }
          if (intervalStart < startTime) {
            startTime = intervalStart;
          }
          if (intervalEnd > endTime) {
            endTime = intervalEnd;
          }
        });
      }

      const start = startTime
        ? this.roundDateTimeToNearestInterval(startTime, true).toFormat('HH:mm:ss')
        : '00:00:00';
      const end = endTime
        ? this.roundDateTimeToNearestInterval(endTime).toFormat('HH:mm:ss')
        : '24:00:00';

      return { start, end };
    },
    useNewGridTilesSetting() {
      return this.$getSettingValue('useNewGridTiles', this.$selectedWarehouse?.settings || null);
    },
    intervalMinutes() {
      /**
       * check if interval minutes is in a 00:00 or 00:00:00 format so
       * we ensure that any logic that comes after this will work as expected
       */
      if (
        typeof this.$intervalMinutes !== 'string' ||
        !/^\d{2}:\d{2}(:\d{2})?$/.test(this.$intervalMinutes)
      ) {
        console.error(
          'intervalMinutes is not in a valid format (',
          this.$intervalMinutes,
          '); using default interval minutes (',
          DEFAULT_INTERVAL_MINUTES,
          ')'
        );
        return DEFAULT_INTERVAL_MINUTES;
      }

      return this.$intervalMinutes;
    },
    intervalMinutesInt() {
      // '00:00' format
      return parseInt(this.intervalMinutes.split(':')[1]);
    },
    warehouseCustomAppointmentStartTimeIntervalsPadded() {
      return this.makeCustomAppointmentStartTimeIntervals(true);
    }
  },
  data() {
    return {
      ready: false,
      showCreateDialog: false,
      showReserveDialog: false,
      appointmentContext: {},
      eventDropInfo: null,
      isConfirming: false,
      processingSchedule: false,
      mounted: false,
      eventContextMenuId: null,
      eventContextMenuEvent: null,
      shouldShowCapacityDockOpts: {},
      shouldShowLoadTypeReselectDialog: false,
      snapOverride: null,
      isLoading: false
    };
  },
  methods: {
    roundDateTimeToNearestInterval(dateTime, roundDown = false) {
      const mathFunc = roundDown ? Math.floor : Math.ceil;
      let minutes = mathFunc(dateTime.minute / this.intervalMinutesInt) * this.intervalMinutesInt;

      // If the time is 23:30 or above, set the minutes to 59 to preserve the correct day/hour
      if (!roundDown && dateTime.hour === 23 && dateTime.minute > 30) {
        // Cap at 59 minutes
        if (dateTime.minute + this.intervalMinutesInt >= 60) {
          minutes = 59;
        }
      }
      return dateTime.set({ minute: minutes, second: 0, millisecond: 0 });
    },
    handleEventDidMount(info) {
      this.addContextListenersToEvent(info);
      const el = info.el;

      const onPointerDown = e => {
        if (e.button !== 0) return; // Only left-click
        if (this.startTimeMinutesSetting !== AppointmentStartTimeMinutes.CUSTOM) {
          const minutes = DateTime.fromISO(info.event.startStr).minute;
          let remainder = minutes % this.startTimeMinutesSetting;
          if (remainder !== 0) {
            // Handle dragging of odd minutes - 10, 20, 5, etc.
            this.snapOverride = `00:${padStart(String(Math.ceil(remainder / 5) * 5), 2, '0')}`;
          } else {
            this.snapOverride = null;
          }
        }
      };

      // Attach the event listeners using arrow functions
      el.addEventListener('pointerdown', onPointerDown);

      // Store the handlers for cleanup
      el._onPointerDown = onPointerDown;
    },
    isSnapTime(timeStr) {
      const minutes = DateTime.fromISO(timeStr).minute;
      let isSnapTime = false;
      switch (this.startTimeMinutesSetting) {
        case 20: {
          isSnapTime = minutes % 20 === 0 || minutes === 0;
          break;
        }
        case 30: {
          isSnapTime = minutes % 30 === 0 || minutes === 0;
          break;
        }
        default: {
          isSnapTime = true;
          break;
        }
      }

      return isSnapTime;
    },
    addContextListenersToEvent(info) {
      if (!info.event.extendedProps.isReserve) {
        info.el.addEventListener('contextmenu', e => {
          e.preventDefault(); // Prevent the default context menu
          this.clearContextMenuData();
          this.setEventContextMenuId(e, info.event);
        });
      }
    },
    getSnapDuration() {
      if (this.startTimeMinutesSetting === AppointmentStartTimeMinutes.CUSTOM) {
        return { minutes: 5 };
      }
      const granularity = this.intervalMinutesInt;
      return { minutes: math.greatestCommonDiv(this.startTimeMinutesSetting, granularity) };
    },
    addCustomClassesToSlotLabels() {
      this.$nextTick(() => {
        // Select all slot elements
        const slotElements = document.querySelectorAll('.fc-timegrid-slot');

        slotElements.forEach(function (slot) {
          const time = slot.getAttribute('data-time'); // e.g., "09:15:00"
          // Example: Add a class to every "inner slot" (20 min, 15 min) that falls between the hour
          // So we can style them differently
          const minutes = parseInt(time.split(':')[1], 10);
          if (minutes !== 0 && (minutes % 15 === 0 || minutes % 20 === 0)) {
            slot.classList.add('inner-slot');
          }
        });
      });
    },
    mergeWarehouseAndDockDaySchedule(warehouseDaySchedule, dockDaySchedule) {
      const warehouseSchedule = warehouseDaySchedule.map(schedule => {
        return this.novaCore.clockSpanToInterval({ start: schedule.start, end: schedule.end }, 1);
      });

      const dockSchedule = dockDaySchedule.map(schedule => {
        return this.novaCore.clockSpanToInterval({ start: schedule.start, end: schedule.end }, 1);
      });

      const intersectedSchedule = this.novaCore.intervalListToClockSpanList(
        this.novaCore.intersectLists(warehouseSchedule, dockSchedule)
      );

      return intersectedSchedule;
    },
    getToFromDates(info) {
      const event = info.event;
      const originalEvent = info.oldEvent;
      const tz = null;
      const fromDate = this.novaCore.getFormattedTime(
        originalEvent.startStr,
        this.novaCore.LuxonDateTimeFormats.LongDateShortMonth
      );
      const fromStart = this.novaCore.formatDateTimeWithMilitarySupport(
        originalEvent.startStr,
        tz,
        this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
        this.$isMilitaryTimeEnabled(this.$org),
        this.novaCore.LuxonDateTimeFormats.Extended24HrTime
      );
      const fromEnd = this.novaCore.formatDateTimeWithMilitarySupport(
        originalEvent.endStr,
        tz,
        this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
        this.$isMilitaryTimeEnabled(this.$org),
        this.novaCore.LuxonDateTimeFormats.Extended24HrTime
      );
      const toDate = this.novaCore.getFormattedTime(
        event.startStr,
        this.novaCore.LuxonDateTimeFormats.LongDateShortMonth
      );
      const toStart = this.novaCore.formatDateTimeWithMilitarySupport(
        event.startStr,
        tz,
        this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
        this.$isMilitaryTimeEnabled(this.$org),
        this.novaCore.LuxonDateTimeFormats.Extended24HrTime
      );
      const toEnd = this.novaCore.formatDateTimeWithMilitarySupport(
        event.endStr,
        tz,
        this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
        this.$isMilitaryTimeEnabled(this.$org),
        this.novaCore.LuxonDateTimeFormats.Extended24HrTime
      );

      return { fromDate, fromStart, fromEnd, toDate, toStart, toEnd };
    },
    buildConfirmationHTML({ to, from }) {
      return `<div>
                <span class="d-block mb-4">You are about to Reschedule this appointment:</span>
                <div class="d-flex">
                <div class="flex-grow-1">
                <div class="font-weight-bold mb-1">FROM:</div>
                <div class="d-flex flex-column">
                <span>${from.date}</span>
                <span>${from.start} - ${from.end}</span>
                <span class="${from.dock ?? 'd-none'}">${from.dock}</span>
                </div>
                </div>
                <div class="flex-grow-1">
                <div class="font-weight-bold mb-1">TO:</div>
                <div class="d-flex flex-column">
                <span>${to.date}</span>
                <span>${to.start} - ${to.end}</span>
                <span class="${to.dock ?? 'd-none'}">${to.dock}</span>
                </div>
                </div>
                </div>
             </div>`;
    },
    isTooltipDisabled(event) {
      return event.isReserve;
    },
    doesDockSupportLoadType(dockId, loadTypeId) {
      const dock = this.$docksKeyedById[dockId];
      if (!dock.loadTypeIds) {
        return true;
      }
      return dock.loadTypeIds?.includes(loadTypeId);
    },
    clearDragDropTimeouts() {
      clearTimeout(this.dragTimeout);
      clearTimeout(this.startTimeout);
    },
    showCreateAppointmentDialog(dayTimeObject, dock) {
      if (!this.selectedWarehouseId) {
        this.$eventHub.$emit('shake-element', 'warehouse-select');
        return;
      }
      if (!this.novaCore.canUserBookInThePast(this.$me)) {
        if (this.checkIfPast(dayTimeObject.date)) {
          this.notify('User Role cannot book appointments in the past', 'info');
          return;
        }
      }
      this.setAppointmentContext(dayTimeObject, dock);
      this.showCreateDialog = true;
    },
    setAppointmentContext(dayTimeObject, dock) {
      const selectedDate = this.findClosestAppointmentStartTime(dayTimeObject);
      this.appointmentContext = {
        selectedDate: DateTime.fromISO(dayTimeObject.date, { zone: this.timezone }).toISO(),
        selectedDocks: [dock],
        selectedWarehouse: this.$selectedWarehouse,
        selectedTime: {
          start: selectedDate
        }
      };
    },
    makeCustomAppointmentStartTimeIntervals(shouldPad = true) {
      const customStartTimes = this.$selectedWarehouse?.settings?.customAppointmentStartTimes;
      if (customStartTimes?.length) {
        const curDate = DateTime.fromISO(this.$selectedDate)
          .setZone(this.timezone)
          .toFormat('yyyy-MM-dd');
        const customStartIntervals = [];

        customStartTimes.forEach(time => {
          const baseDateTime = DateTime.fromFormat(`${curDate} ${time}`, 'yyyy-MM-dd H:mm', {
            zone: this.$selectedWarehouse.timezone,
            keepLocalTime: true
          });

          const startDateTime = shouldPad
            ? baseDateTime.minus({ minutes: customStartTimeIntervalBuffer })
            : baseDateTime;

          const endDateTime = shouldPad
            ? baseDateTime.plus({ minutes: customStartTimeIntervalBuffer })
            : baseDateTime;

          customStartIntervals.push(Interval.fromDateTimes(startDateTime, endDateTime));
        });

        return customStartIntervals;
      }

      return [];
    },
    findClosestAppointmentStartTime(dateTimeObject) {
      const luxonObj = DateTime.fromJSDate(dateTimeObject.date, { zone: this.timezone });
      let result = luxonObj;
      const startTimeMinutes = this.startTimeMinutesSetting;

      if (
        this.areCustomStartTimesEnabled &&
        !this.warehouseCustomAppointmentStartTimeIntervalsPadded.length
      ) {
        return result;
      }

      if (this.areCustomStartTimesEnabled) {
        const nearestCustomStartTime = this.makeCustomAppointmentStartTimeIntervals(false).reduce(
          (closest, current) => {
            if (!closest) return current;

            const targetTime = luxonObj.set({ second: 0, millisecond: 0 }).toMillis();
            const currentTime = current.start.set({ second: 0, millisecond: 0 }).toMillis();
            const closestTime = closest.start.set({ second: 0, millisecond: 0 }).toMillis();

            return Math.abs(currentTime - targetTime) < Math.abs(closestTime - targetTime)
              ? current
              : closest;
          },
          null
        );
        result = nearestCustomStartTime.start;
      } else {
        const totalMinutes = luxonObj.hour * 60 + luxonObj.minute;
        const roundedMinutes = Math.floor(totalMinutes / startTimeMinutes) * startTimeMinutes;
        result = luxonObj.startOf('day').plus({ minutes: roundedMinutes });
      }

      return result;
    },
    checkIfPast(date) {
      if (!(date && this.timezone)) {
        return true;
      }
      let curDate = DateTime.now().setZone(this.$selectedWarehouse.timezone).startOf('day');
      let checkDate = DateTime.fromISO(date, { zone: this.timezone }).startOf('day');
      return checkDate < curDate;
    },
    handleDialogClose(dialogToClose) {
      this[dialogToClose] = false;
    },
    async handleEventClick(clickInfo) {
      const plainEvent = clickInfo.event.toPlainObject();
      const { extendedProps, ...topLevelProps } = plainEvent;
      const event = { ...extendedProps, ...topLevelProps };
      this.clearContextMenuData();
      this.$store.commit('Calendar/setSelectedEvent', event);
      if (this.novaCore.isReserve(event)) {
        this.showReserveDialog = this.$rolePermissions.canUpdateAppointment;
      } else {
        this.showDetailsDialog = true;
        await this.$router.replace({ query: { appointmentId: event.id } });
      }
    },
    async handleEventDrop(eventDropInfo) {
      const newEvent = eventDropInfo.event;
      const oldEvent = eventDropInfo.oldEvent;

      if (this.areCustomStartTimesEnabled) {
        const proposedStartDateTime = DateTime.fromISO(newEvent.startStr, {
          zone: this.timezone
        });

        // Initialize variables to track the closest custom appointment start time
        let smallestDifference = Infinity;
        let closestCustomAppointmentStartDateTime = null;

        for (const interval of this.warehouseCustomAppointmentStartTimeIntervalsPadded) {
          if (interval.contains(proposedStartDateTime)) {
            /**
             * To get the custom appointment start date time, we have to find the midpoint of the
             * interval with a buffer of {customStartTimeIntervalBuffer} minutes on either side.
             */
            const customStartDateTime = calculateIntervalMidpoint(interval);

            const differenceInMinutes = Math.abs(
              proposedStartDateTime.diff(customStartDateTime, 'minutes').minutes
            );

            // Update the closest midpoint if this difference is smaller
            if (differenceInMinutes < smallestDifference) {
              smallestDifference = differenceInMinutes;
              closestCustomAppointmentStartDateTime = customStartDateTime;
            }
          }
        }

        // If a closest midpoint was found within the custom intervals
        if (closestCustomAppointmentStartDateTime) {
          // Calculate the duration of the event in minutes
          const eventDurationInMinutes = DateTime.fromISO(newEvent.endStr).diff(
            DateTime.fromISO(newEvent.startStr),
            ['minutes']
          ).minutes;

          // Update the event's start and end times to snap to the closest custom appointment start time
          newEvent.setStart(closestCustomAppointmentStartDateTime.toJSDate());

          if (eventDurationInMinutes > 0) {
            const newEndDateTime = closestCustomAppointmentStartDateTime.plus({
              minutes: eventDurationInMinutes
            });
            eventDropInfo.event.setEnd(newEndDateTime.toJSDate());
          }
        } else {
          this.notify(
            'Appointment must be dropped on a custom appointment start time (See Appointment Start Times in Settings)',
            'error'
          );
          eventDropInfo.revert();
          return; // Exit early as further processing is unnecessary
        }
      }

      // Determine the dock ID and load type ID
      const dockId = eventDropInfo.newResource?.id ?? newEvent.extendedProps.dockId;
      const loadTypeId = oldEvent.extendedProps.loadTypeId;

      // Check if the dock supports the load type
      const dockSupportsLoadType = !dockId || this.doesDockSupportLoadType(dockId, loadTypeId);

      if (dockSupportsLoadType) {
        this.clearNotifications();
      }
      // If the event's start time and resource haven't changed, no further action is needed
      if (
        newEvent.startStr === oldEvent.startStr &&
        eventDropInfo.newResource?.id === eventDropInfo.oldResource?.id
      ) {
        this.getWarehouseBusinessHours?.();
        return;
      }

      // Handle load type re-selection or confirm time change based on dock support
      if (eventDropInfo.event.extendedProps.loadTypeId && !dockSupportsLoadType) {
        await this.showLoadTypeReselect(eventDropInfo);
      } else {
        this.confirmTimeChange(eventDropInfo);
      }
    },
    confirmTimeChange(info) {
      // TODO: optimize and cleanup?
      const eventId = info.event.id;
      const start = info.event.startStr;
      const toDockId = info.newResource?.id ?? info.event.extendedProps.dockId;
      const fromDockId = info.oldResource?.id ?? info.event.extendedProps.dockId;
      const { fromDate, fromStart, fromEnd, toDate, toStart, toEnd } = this.getToFromDates(info);
      const to = {
        date: toDate,
        start: toStart,
        end: toEnd,
        dock: this.$docksKeyedById[toDockId].name
      };
      const from = {
        date: fromDate,
        start: fromStart,
        end: fromEnd,
        dock: this.$docksKeyedById[fromDockId].name
      };

      this.$confirm(
        this.buildConfirmationHTML({
          to,
          from
        }),
        {
          buttonTrueText: 'Reschedule',
          title: 'Reschedule Appointment'
        }
      ).then(async confirmed => {
        if (confirmed) {
          if (this.isLoading) {
            return;
          }
          this.isLoading = true;
          await axios
            .patch(`appointment/${eventId}`, {
              start,
              dockId: toDockId
            })
            .then(async response => {
              const eventId = response?.data?.data?.id;
              if (eventId) {
                this.trackDragAndDropMixpanelEvent(response.data.data);
                this.$store.commit('Calendar/removeEvent', eventId);
                this.$store.dispatch('Appointments/triggerSocketEvent', {
                  response,
                  appointment: this.reserve
                });

                this.$eventHub.$emit('appointment-updated');
              }
            })
            .catch(e => {
              info.revert();
              if (!/full|conflict/.test(e?.response?.data?.message)) {
                this.notify(
                  'Unable to reschedule event, please refresh the page and try again.',
                  'error'
                );
              }
            })
            .finally(() => {
              this.isConfirming = false;
              this.isLoading = false;
            });
        } else {
          this.getWarehouseBusinessHours?.();
          info.revert();
          this.isConfirming = false;
          this.isLoading = false;
        }
      });
    },
    confirmDurationChange(info) {
      // TODO: optimize and cleanup?

      const eventId = info.event.id;
      const start = this.novaCore.renderTimezoneInUtc(info.event.startStr, this.timezone);
      const end = this.novaCore.renderTimezoneInUtc(info.event.endStr, this.timezone);
      const { fromDate, fromStart, fromEnd, toDate, toStart, toEnd } = this.getToFromDates(info);

      const to = {
        date: toDate,
        start: toStart,
        end: toEnd
      };
      const from = {
        date: fromDate,
        start: fromStart,
        end: fromEnd
      };

      this.$confirm(
        this.buildConfirmationHTML({
          to,
          from
        }),
        {
          buttonTrueText: 'Update',
          title: 'Update Appointment Duration'
        }
      ).then(async confirmed => {
        if (confirmed) {
          if (this.isLoading) {
            return;
          }
          this.isLoading = true;
          await axios
            .patch(`appointment/${eventId}`, {
              start,
              end
            })
            .then(async response => {
              const eventId = response?.data?.data?.id;
              if (eventId) {
                this.trackDragAndDropMixpanelEvent(response.data.data);
                this.$store.commit('Calendar/removeEvent', eventId);
                this.$store.dispatch('Appointments/triggerSocketEvent', {
                  response,
                  appointment: this.reserve
                });
                this.$eventHub.$emit('appointment-updated');
              }
            })
            .catch(() => {
              info.revert();
              this.notify(
                'Unable to change event duration, please refresh the page and try again.',
                'error'
              );
            })
            .finally(() => {
              this.getWarehouseBusinessHours?.();
              this.isConfirming = false;
              this.isLoading = false;
            });
        } else {
          this.getWarehouseBusinessHours?.();
          info.revert();
          this.isConfirming = false;
          this.isLoading = false;
        }
      });
    },
    hasDockScheduleChanged(selectedDock, dock) {
      return selectedDock?.id && !isEqual(selectedDock?.schedule, dock?.schedule);
    },
    async handleSocketDockUpdate(dock) {
      const warehouseId = get(dock, 'warehouseId');
      if (warehouseId && this.selectedWarehouseId && warehouseId !== this.selectedWarehouseId) {
        return;
      }
      const selectedDock = this.$selectedDocks.find(selectedDock => {
        return selectedDock.id === dock.id;
      });
      if (this.hasDockScheduleChanged(selectedDock, dock) && this.selectedWarehouseId) {
        const warehouse = await this.$store.dispatch(
          'Warehouses/getWarehouseById',
          this.selectedWarehouseId
        );
        this.isLoading = true;
        await this.$store.dispatch('Calendar/setSelectedWarehouse', warehouse);
        const selectedDockIds = this.novaCore.pluck(this.$selectedDocks, 'id');
        const newSelectedDocks = warehouse.docks.filter(dock => {
          return selectedDockIds.includes(dock.id);
        });
        this.$store.commit('Calendar/setSelectedDocks', newSelectedDocks);
        await this.$store.dispatch('Calendar/getEvents', {});
        this.notify(`Dock ${dock.name}'s schedule has been updated`, 'info');
      }
      this.$nextTick(() => (this.isLoading = false));
    },
    async handleSocketWarehouseUpdate(event) {
      if (this.selectedWarehouseId !== event.id) {
        return;
      }
      if (isEqual(this.$selectedWarehouse?.schedule, event?.schedule)) {
        return;
      }

      const warehouse = await this.$store.dispatch(
        'Warehouses/getWarehouseById',
        this.selectedWarehouseId
      );
      await this.$store.dispatch('Calendar/setSelectedWarehouse', warehouse);
      await this.$store.dispatch('Calendar/getEvents', {});
      this.notify(`Warehouse ${warehouse.name}'s schedule has been updated`, 'info');
    },
    async handleSocketTriggerUpdate() {
      if (this.selectedWarehouseId) {
        return this.$store.dispatch('Calendar/getWarehouseTriggers', {
          warehouseId: this.selectedWarehouseId
        });
      }
    },
    cancelReserve(event) {
      this.$store.dispatch('Appointments/deleteReserve', { id: event.id });
    },
    setEventContextMenuId(e, event) {
      this.eventContextMenuId = event.id;
      this.eventContextMenuEvent = e;
    },
    nevermindLoadTypeDialog() {
      this.shouldShowLoadTypeReselectDialog = false;
      this.eventDropInfo?.revert?.();
      this.eventDropInfo = null;
    },
    async showLoadTypeReselect(eventDropInfo) {
      this.eventDropInfo = eventDropInfo;
      this.shouldShowLoadTypeReselectDialog = true;
    },
    trackDragAndDropMixpanelEvent(appointment) {
      this.mixpanel.track(this.mixpanel.events.MODULE.APPOINTMENT.RESCHEDULED, {
        'Org Name': this.$org.name,
        'Org ID': this.$org.id,
        'Appointment ID': appointment.id,
        'Appointment Start': appointment.start,
        'Entry Point': 'Drag and Drop',
        'Appointment ETA': appointment.eta,
        'Warehouse ID': this.$selectedWarehouse.id,
        'Warehouse Name': this.$selectedWarehouse.name
      });
    },
    isStartingStatus(status) {
      return this.novaCore.isStartStatus(status);
    },
    clearContextMenuData() {
      this.eventContextMenuEvent = null;
      this.eventContextMenuId = null;
    }
  },
  async mounted() {
    await this.handleApptIdURLQuery();
    if (!this.$selectedWarehouse || !this.$selectedDocks) {
      this.util.addCursorTextListener(
        this.$refs.calendar.$el,
        'No Warehouse/Docks selected',
        'calendarCursorText'
      );
    }
    this.$eventHub.$on('update-Dock', this.handleSocketDockUpdate);
    this.$eventHub.$on('update-Warehouse', this.handleSocketWarehouseUpdate);

    this.$eventHub.$on('update-Trigger', this.handleSocketTriggerUpdate);
    this.$eventHub.$on('create-Trigger', this.handleSocketTriggerUpdate);
  },
  beforeUnmount() {
    this.$eventHub.$off('update-Dock', this.handleSocketDockUpdate);
    this.$eventHub.$off('update-Warehouse', this.handleSocketWarehouseUpdate);

    this.$eventHub.$off('update-Trigger');
    this.$eventHub.$off('create-Trigger');
  },
  watch: {
    $isFetchingEvents(val) {
      this.$emit('set-loader', val);
    },
    $intervalMinutes() {
      this.addCustomClassesToSlotLabels();
    },
    $zoomLevel() {
      this.$nextTick(() => {
        // Rerenders current date
        this.calendarApi?.gotoDate(this.$selectedDate);
      });
    },
    ready(ready) {
      if (ready) {
        this.$store.commit('Calendar/setCalendar', this.calendarApi);
      }
    },
    $route(newVal, oldVal) {
      this.$nextTick(async () => {
        const shouldFetchEvent =
          newVal.query.appointmentId &&
          newVal.query.appointmentId !== oldVal.query.appointmentId &&
          this.$selectedEvent?.id !== newVal.query.appointmentId;
        if (shouldFetchEvent) {
          await this.fetchAndOpenAppt(newVal.query.appointmentId);
        }
      });
    },
    '$i18n.locale': {
      handler(newLocale) {
        this.calendarApi?.setOption('locale', newLocale);
      },
      immediate: true
    }
  }
};
